/* 
  HAPPE BOX V3 - Gift Builder Core Engine
  Handles State Management, Builder API Layer, Dynamic Step Layout Rendering,
  Step Validation, LocalStorage Persistence, and Dynamic Pricing calculations.
*/

// ==========================================
// 1. Centralized State Manager
// ==========================================
class BuilderStateManager {
  constructor() {
    this.storageKey = 'hp_builder_state_v3';
    this.schemaVersion = '1.0';
    this.state = this.loadState() || this.getInitialState();
  }

  getInitialState() {
    return {
      version: this.schemaVersion,
      base: null,          // { id, title, price, overlay_image }
      flowers: [],         // Array of selected items
      cake: null,          // Selected cake
      chocolates: [],      // Array of chocolates
      balloons: [],        // Array of balloons
      extras: [],          // Array of extras (LEDs, perfumes)
      custom_items: [],    // Array of custom items (photo frames, toppers)
      personalization: {
        message: '',
        sender: '',
        recipient: '',
        photo_url: '',
        video_url: ''
      },
      delivery: {
        date: null,
        slot: null,
        phone: '',
        city: 'Doha'
      }
    };
  }

  saveState() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    this.dispatchUpdate();
  }

  loadState() {
    try {
      const data = localStorage.getItem(this.storageKey);
      if (!data) return null;
      const parsed = JSON.parse(data);
      if (parsed.version !== this.schemaVersion) {
        console.warn('Migration needed. Resetting state.');
        return null;
      }
      // Ensure all new keys exist to prevent crashes from older session data
      const initial = this.getInitialState();
      const merged = Object.assign({}, initial, parsed);
      
      // Ensure all array properties are actual arrays to prevent legacy crashes
      merged.flowers = Array.isArray(merged.flowers) ? merged.flowers : (merged.flowers ? [merged.flowers] : []);
      merged.chocolates = Array.isArray(merged.chocolates) ? merged.chocolates : (merged.chocolates ? [merged.chocolates] : []);
      merged.balloons = Array.isArray(merged.balloons) ? merged.balloons : (merged.balloons ? [merged.balloons] : []);
      merged.extras = Array.isArray(merged.extras) ? merged.extras : (merged.extras ? [merged.extras] : []);
      merged.custom_items = Array.isArray(merged.custom_items) ? merged.custom_items : (merged.custom_items ? [merged.custom_items] : []);

      // Clear out legacy mock choices from storage
      if (merged.base && String(merged.base.id).startsWith('base_')) merged.base = null;
      if (merged.cake && String(merged.cake.id).startsWith('cake_')) merged.cake = null;
      
      merged.flowers = merged.flowers.filter(item => item && !String(item.id).startsWith('flower_'));
      merged.chocolates = merged.chocolates.filter(item => item && !String(item.id).startsWith('choco_'));
      merged.balloons = merged.balloons.filter(item => item && !String(item.id).startsWith('balloon_'));
      merged.extras = merged.extras.filter(item => item && !String(item.id).startsWith('extra_'));
      merged.custom_items = merged.custom_items.filter(item => item && !String(item.id).startsWith('custom_'));
      
      merged.personalization = Object.assign({}, initial.personalization, parsed.personalization);
      merged.delivery = Object.assign({}, initial.delivery, parsed.delivery);
      return merged;
    } catch (e) {
      console.error('Error loading builder state', e);
      return null;
    }
  }

  clearState() {
    localStorage.removeItem(this.storageKey);
    this.state = this.getInitialState();
    this.saveState();
  }

  dispatchUpdate() {
    const event = new CustomEvent('builder:state-change', { detail: this.state });
    document.dispatchEvent(event);
  }
}

// Instantiate Global State
window.BuilderState = new BuilderStateManager();


// ==========================================
// 2. Unified Builder API Layer
// ==========================================
window.BuilderAPI = {
  selectBase(item) {
    window.BuilderState.state.base = item;
    window.BuilderState.saveState();
  },

  toggleItem(type, item, maxSelections = 1) {
    const state = window.BuilderState.state;
    let prop = type;
    if (type === 'chocolate') prop = 'chocolates';
    if (type === 'balloon') prop = 'balloons';
    if (type === 'custom-items' || type === 'custom_items') prop = 'custom_items';

    const arrayProps = ['flowers', 'chocolates', 'balloons', 'extras', 'custom_items'];
    const isArrayProp = arrayProps.includes(prop);

    if (isArrayProp) {
      if (!Array.isArray(state[prop])) {
        state[prop] = [];
      }
      const existingIdx = state[prop].findIndex(i => i.id === item.id);
      if (existingIdx > -1) {
        state[prop].splice(existingIdx, 1);
      } else {
        if (maxSelections === 1) {
          state[prop] = [item];
        } else {
          if (state[prop].length < maxSelections) {
            state[prop].push(item);
          } else {
            // If limit reached, swap the oldest selection
            state[prop].shift();
            state[prop].push(item);
          }
        }
      }
    } else {
      // Single object property (like base, cake)
      if (state[prop] && state[prop].id === item.id) {
        state[prop] = null;
      } else {
        state[prop] = item;
      }
    }
    window.BuilderState.saveState();
  },

  setPersonalization(field, value) {
    window.BuilderState.state.personalization[field] = value;
    window.BuilderState.saveState();
  },

  setDelivery(field, value) {
    window.BuilderState.state.delivery[field] = value;
    window.BuilderState.saveState();
  },

  calculateSubtotal() {
    const state = window.BuilderState.state;
    let total = 0;
    if (state.base) total += parseFloat(state.base.price || 0);
    if (Array.isArray(state.flowers)) {
      state.flowers.forEach(f => total += parseFloat(f.price || 0));
    }
    if (state.cake) total += parseFloat(state.cake.price || 0);
    if (Array.isArray(state.chocolates)) {
      state.chocolates.forEach(c => total += parseFloat(c.price || 0));
    }
    if (Array.isArray(state.balloons)) {
      state.balloons.forEach(b => total += parseFloat(b.price || 0));
    }
    if (Array.isArray(state.extras)) {
      state.extras.forEach(e => total += parseFloat(e.price || 0));
    }
    if (Array.isArray(state.custom_items)) {
      state.custom_items.forEach(ci => total += parseFloat(ci.price || 0));
    }
    return total;
  },

  calculateTotal() {
    const subtotal = this.calculateSubtotal();
    // Default delivery fee in Qatar (e.g. Doha = free, outer cities = QR 30)
    const city = window.BuilderState.state.delivery.city;
    const deliveryFee = (city && city !== 'Doha') ? 30.00 : 0.00;
    return subtotal + deliveryFee;
  },

  async addToCart() {
    const state = window.BuilderState.state;
    if (!state.base) {
      alert('Please choose a base box to continue.');
      return;
    }

    const builderId = `HB-${Date.now()}`;
    const cartItems = [];

    // 1. Base Box
    cartItems.push({
      id: state.base.variant_id,
      quantity: 1,
      properties: this.getPropertiesMap(builderId, 'Base Box')
    });

    // 2. Flowers
    if (Array.isArray(state.flowers)) {
      state.flowers.forEach(item => {
        cartItems.push({
          id: item.variant_id,
          quantity: 1,
          properties: this.getPropertiesMap(builderId, 'Flowers')
        });
      });
    }

    // 3. Cake
    if (state.cake) {
      cartItems.push({
        id: state.cake.variant_id,
        quantity: 1,
        properties: this.getPropertiesMap(builderId, 'Cake')
      });
    }

    // 4. Chocolates
    if (Array.isArray(state.chocolates)) {
      state.chocolates.forEach(item => {
        cartItems.push({
          id: item.variant_id,
          quantity: 1,
          properties: this.getPropertiesMap(builderId, 'Chocolates')
        });
      });
    }

    // 5. Balloons
    if (Array.isArray(state.balloons)) {
      state.balloons.forEach(item => {
        cartItems.push({
          id: item.variant_id,
          quantity: 1,
          properties: this.getPropertiesMap(builderId, 'Balloons')
        });
      });
    }

    // 6. Extras
    if (Array.isArray(state.extras)) {
      state.extras.forEach(item => {
        cartItems.push({
          id: item.variant_id,
          quantity: 1,
          properties: this.getPropertiesMap(builderId, 'Extras')
        });
      });
    }

    // 7. Custom Items
    if (Array.isArray(state.custom_items)) {
      state.custom_items.forEach(item => {
        cartItems.push({
          id: item.variant_id,
          quantity: 1,
          properties: this.getPropertiesMap(builderId, 'Custom Items')
        });
      });
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });
      if (response.ok) {
        // Clear local storage state on success
        window.BuilderState.clearState();
        const targetCartUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
          ? (window.Shopify.routes.root + 'cart').replace('//', '/')
          : (window.location.pathname.startsWith('/ar') ? '/ar/cart' : '/cart');
        window.location.href = targetCartUrl;
      } else {
        const err = await response.json();
        console.error('Failed to add custom bundle to cart', err);
        alert('There was a problem creating your custom box. Please try again.');
      }
    } catch (e) {
      console.error('Connection error adding items', e);
    }
  },

  getPropertiesMap(builderId, stepName) {
    const state = window.BuilderState.state;
    return {
      "_builder_id": builderId,
      "_builder_name": `Custom Gift - ${state.base ? state.base.title : 'Bundle'}`,
      "_builder_step": stepName,
      "_delivery_date": state.delivery.date || 'TBD',
      "_delivery_slot": state.delivery.slot || 'Standard',
      "_recipient_phone": state.delivery.phone || 'N/A'
    };
  }
};


// ==========================================
// 3. Component Event Bus & Orchestrator
// ==========================================
class GiftBuilderUIOrchestrator {
  constructor() {
    this.currentStepIndex = 0;
    this.steps = [];
    this.activeFilters = { ideas: 'All', occasion: 'All', recipient: 'All' };
    this.activeSort = 'relevance';
    this.loadData();
    this.initElements();
    this.attachEvents();
    this.handleUrlPreselect();
    this.render();
  }

  handleUrlPreselect() {
    const params = new URLSearchParams(window.location.search);
    let stateChanged = false;
    
    const preselectTypes = ['base', 'flowers', 'cake', 'chocolate', 'balloon', 'extras'];
    
    // Support single preselect query param
    const singlePreselect = params.get('preselect');
    if (singlePreselect) {
      const optionInfo = this.findOptionByHandleOrId(singlePreselect);
      if (optionInfo) {
        this.applyPreselectedOption(optionInfo.option, optionInfo.stepType, optionInfo.maxSelections);
        stateChanged = true;
      }
    }
    
    // Support specific step preselect query params
    preselectTypes.forEach(type => {
      const val = params.get(`preselect_${type}`) || params.get(`preselect_${type}s`);
      if (val) {
        const handles = val.split(',');
        handles.forEach(handle => {
          const optionInfo = this.findOptionByHandleOrId(handle, type);
          if (optionInfo) {
            this.applyPreselectedOption(optionInfo.option, optionInfo.stepType, optionInfo.maxSelections);
            stateChanged = true;
          }
        });
      }
    });

    if (stateChanged) {
      window.BuilderState.saveState();
      // Wait for DOM to finish rendering initial state
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('builder:state-change', { detail: window.BuilderState.state }));
      }, 100);
    }
  }

  findOptionByHandleOrId(val, filterType = null) {
    for (const step of this.steps) {
      if (filterType && step.type !== filterType) continue;
      if (step.options && step.options.length > 0) {
        const option = step.options.find(opt => 
          opt.handle === val || 
          String(opt.id) === val ||
          (opt.title && opt.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') === val)
        );
        if (option) {
          return {
            option,
            stepType: step.type,
            maxSelections: step.max_selections || 1
          };
        }
      }
    }
    return null;
  }

  applyPreselectedOption(option, stepType, maxSelections) {
    const state = window.BuilderState.state;
    let prop = stepType === 'base' ? 'base' : stepType;
    if (prop === 'chocolate') prop = 'chocolates';
    if (prop === 'balloon') prop = 'balloons';
    if (prop === 'custom-items' || prop === 'custom_items') prop = 'custom_items';

    const arrayProps = ['flowers', 'chocolates', 'balloons', 'extras', 'custom_items', 'gift_items'];
    const isArrayProp = arrayProps.includes(prop);

    if (isArrayProp) {
      if (!Array.isArray(state[prop])) {
        state[prop] = state[prop] ? [state[prop]] : [];
      }
      const alreadySelected = state[prop].some(item => item && item.id === option.id);
      if (!alreadySelected) {
        if (maxSelections === 1) {
          state[prop] = [option];
        } else {
          if (state[prop].length >= maxSelections) {
            state[prop].shift();
          }
          state[prop].push(option);
        }
      }
      console.log(`Preselected option for ${stepType}:`, option.title);
    } else {
      state[prop] = option;
      console.log(`Preselected single option for ${stepType}:`, option.title);
    }
  }

  loadData() {
    try {
      const scriptEl = document.getElementById('gifting-builder-steps-data');
      if (scriptEl) {
        const payload = JSON.parse(scriptEl.textContent);
        this.steps = payload.steps || [];
        this.deliverySlots = payload.delivery_slots || [];
      }
    } catch (e) {
      console.error('Failed to parse builder steps payload', e);
    }
  }

  initElements() {
    this.stepTitle = document.getElementById('step-title');
    this.stepIndicator = document.getElementById('step-indicator');
    this.stepPercentage = document.getElementById('step-percentage');
    this.progressBar = document.getElementById('progress-bar');
    this.stepContent = document.getElementById('step-content');
    
    this.prevBtn = document.getElementById('prev-step');
    this.nextBtn = document.getElementById('next-step');
    this.skipBtn = document.getElementById('skip-step');
    this.addBtn = document.getElementById('add-to-cart-builder');

    this.totalPriceEl = document.getElementById('total-price');
    this.mobileTotalPriceEl = document.getElementById('mobile-total-price');
    this.mobileDrawerTotal = document.getElementById('mobile-drawer-total');
    
    this.summaryList = document.getElementById('summary-list');
    this.mobileSummaryList = document.getElementById('mobile-summary-list');
    this.mobileSummaryDrawer = document.getElementById('mobileSummaryDrawer');
    this.mobileSummaryOverlay = document.getElementById('mobileSummaryOverlay');
    this.mobileToggleBtn = document.getElementById('mobile-toggle-drawer');
    this.closeSummaryBtn = document.getElementById('close-summary-drawer');
    this.filterBtn = document.getElementById('filter-dropdown-btn');
    this.filterPanel = document.getElementById('filter-flyout-panel');
    this.filterCountBadge = document.getElementById('active-filters-count');
    this.clearFiltersBtn = document.getElementById('clear-all-filters');
    this.closeFiltersBtn = document.getElementById('close-filter-flyout');
    this.checkboxes = document.querySelectorAll('.filter-checkbox');

    // Sort Drawer Elements
    this.sortBtn = document.getElementById('sort-dropdown-btn');
    this.sortLabel = document.getElementById('builder-active-sort-label');
    this.builderSortDrawer = document.getElementById('builderSortDrawer');
    this.builderSortOverlay = document.getElementById('builderSortOverlay');
    this.closeSortBtn = document.getElementById('close-builder-sort-drawer');
    this.sortOptionRows = document.querySelectorAll('#builderSortDrawer .sort-option-row');

    if (this.builderSortDrawer && this.builderSortDrawer.parentElement !== document.body) {
      if (this.builderSortOverlay) document.body.appendChild(this.builderSortOverlay);
      document.body.appendChild(this.builderSortDrawer);
    }
  }

  attachEvents() {
    // State Changes updates layout dynamically
    document.addEventListener('builder:state-change', () => {
      this.updatePricing();
      this.updateSummary();
      this.validateCurrentStep();
    });

    // Stepper Navigation
    this.nextBtn.addEventListener('click', () => this.navigateStep(1));
    this.prevBtn.addEventListener('click', () => this.navigateStep(-1));
    this.skipBtn.addEventListener('click', () => this.navigateStep(1));
    this.addBtn.addEventListener('click', () => window.BuilderAPI.addToCart());

    // Mobile Drawer events
    if (this.mobileToggleBtn) {
      this.mobileToggleBtn.addEventListener('click', () => this.toggleMobileDrawer(true));
    }
    if (this.closeSummaryBtn) {
      this.closeSummaryBtn.addEventListener('click', () => this.toggleMobileDrawer(false));
    }
    if (this.mobileSummaryOverlay) {
      this.mobileSummaryOverlay.addEventListener('click', () => this.toggleMobileDrawer(false));
    }
    const footerTrigger = document.getElementById('footer-price-trigger');
    if (footerTrigger) {
      footerTrigger.addEventListener('click', () => this.toggleMobileDrawer(true));
    }

    // Sort Drawer events
    if (this.sortBtn) {
      this.sortBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleSortDrawer(true);
      });
    }
    if (this.closeSortBtn) {
      this.closeSortBtn.addEventListener('click', () => this.toggleSortDrawer(false));
    }
    if (this.builderSortOverlay) {
      this.builderSortOverlay.addEventListener('click', () => this.toggleSortDrawer(false));
    }

    if (this.sortOptionRows) {
      this.sortOptionRows.forEach(row => {
        row.addEventListener('click', () => {
          this.sortOptionRows.forEach(r => r.classList.remove('is-selected'));
          row.classList.add('is-selected');
          const sortVal = row.getAttribute('data-sort-val');
          if (sortVal) {
            this.activeSort = sortVal;
            const labelText = row.querySelector('.sort-option-label')?.textContent || 'Sort By';
            if (this.sortLabel) this.sortLabel.textContent = labelText;
            this.toggleSortDrawer(false);
            const step = this.steps[this.currentStepIndex];
            this.renderStepContent(step);
          }
        });
      });
    }
    
    // Preview Image link toggle events
    document.addEventListener('click', (e) => {
      const link = e.target.closest('.summary-item-link');
      if (link) {
        e.preventDefault();
        const imageSrc = link.getAttribute('data-image');
        if (imageSrc && window.PreviewRenderer) {
          window.PreviewRenderer.showImage(imageSrc);
        }
      }
    });

    // Toggle filter dropdown flyout panel
    if (this.filterBtn && this.filterPanel) {
      this.filterBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = this.filterPanel.style.display === 'block';
        this.filterPanel.style.display = isOpen ? 'none' : 'block';
        this.filterBtn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    }

    // Close panel when clicking Apply/Close button
    if (this.closeFiltersBtn) {
      this.closeFiltersBtn.addEventListener('click', () => {
        if (this.filterPanel) this.filterPanel.style.display = 'none';
        if (this.filterBtn) this.filterBtn.setAttribute('aria-expanded', 'false');
      });
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.filterPanel && !this.filterPanel.contains(e.target) && e.target !== this.filterBtn && !this.filterBtn.contains(e.target)) {
        this.filterPanel.style.display = 'none';
        if (this.filterBtn) this.filterBtn.setAttribute('aria-expanded', 'false');
      }
    });

    // Clear all checked options
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener('click', () => {
        this.checkboxes.forEach(cb => cb.checked = false);
        this.activeFilters = { ideas: [], occasion: [], recipient: [] };
        this.updateFiltersBadge();
        const step = this.steps[this.currentStepIndex];
        this.renderStepContent(step);
      });
    }

    // Capture checkbox selection updates
    this.checkboxes.forEach(cb => {
      cb.addEventListener('change', () => {
        const ideas = [];
        const occasion = [];
        const recipient = [];

        this.checkboxes.forEach(c => {
          if (c.checked) {
            if (c.name === 'ideas') ideas.push(c.value);
            if (c.name === 'occasions') occasion.push(c.value);
            if (c.name === 'recipients') recipient.push(c.value);
          }
        });

        this.activeFilters = { ideas, occasion, recipient };
        this.updateFiltersBadge();
        const step = this.steps[this.currentStepIndex];
        this.renderStepContent(step);
      });
    });

    // Accordion headers toggle (show/hide checklists)
    const accordionHeaders = document.querySelectorAll('.filter-accordion-header');
    accordionHeaders.forEach(header => {
      header.addEventListener('click', (e) => {
        e.stopPropagation();
        const content = header.nextElementSibling;
        const chevron = header.querySelector('.accordion-chevron');
        const isCollapsed = content.style.display === 'none';
        
        if (isCollapsed) {
          content.style.display = 'flex';
          chevron.style.transform = 'rotate(0deg)';
        } else {
          content.style.display = 'none';
          chevron.style.transform = 'rotate(180deg)';
        }
      });
    });
  }

  toggleSortDrawer(isOpen) {
    if (!this.builderSortDrawer || !this.builderSortOverlay) return;
    if (isOpen) {
      if (this.builderSortDrawer.parentElement !== document.body) {
        document.body.appendChild(this.builderSortOverlay);
        document.body.appendChild(this.builderSortDrawer);
      }
      this.builderSortDrawer.classList.add('is-open');
      this.builderSortOverlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    } else {
      this.builderSortDrawer.classList.remove('is-open');
      this.builderSortOverlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    }
  }

  navigateStep(direction) {
    const nextIdx = this.currentStepIndex + direction;
    if (nextIdx >= 0 && nextIdx < this.steps.length) {
      this.currentStepIndex = nextIdx;
      this.activeOccasionFilter = 'All';
      this.render();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToStepType(type) {
    if (!type || !this.steps) return;
    let prop = type;
    if (type === 'chocolate') prop = 'chocolates';
    if (type === 'balloon') prop = 'balloons';
    if (type === 'custom-items') prop = 'custom_items';

    const targetIdx = this.steps.findIndex(s => 
      s.type === type || 
      s.type === prop || 
      (s.type === 'custom_items' && prop === 'custom_items') ||
      (s.type === 'chocolates' && prop === 'chocolates') ||
      (s.type === 'balloons' && prop === 'balloons') ||
      (s.type === 'extras' && prop === 'extras')
    );

    if (targetIdx !== -1) {
      this.currentStepIndex = targetIdx;
      this.activeOccasionFilter = 'All';
      this.render();
      const mainEl = document.getElementById('step-content') || document.querySelector('.builder-main');
      if (mainEl) mainEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  renderSidebarStepper() {
    const sidebar = document.getElementById('SidebarStepperNav');
    const mobileNav = document.getElementById('MobileStepperNav');
    if (!sidebar && !mobileNav) return;

    if (sidebar) sidebar.innerHTML = '';
    if (mobileNav) mobileNav.innerHTML = '';
    const state = window.BuilderState.state;

    this.steps.forEach((step, idx) => {
      // Determine step status
      let isCompleted = false;
      let isActive = idx === this.currentStepIndex;
      
      // Check if step is completed
      let prop = step.type;
      if (prop === 'chocolate') prop = 'chocolates';
      if (prop === 'balloon') prop = 'balloons';
      
      if (step.type === 'base') {
        isCompleted = !!state.base;
      } else if (step.type === 'personalization') {
        isCompleted = !!state.personalization && !!state.personalization.recipient;
      } else if (step.type === 'delivery') {
        isCompleted = !!state.delivery && !!state.delivery.date && !!state.delivery.slot;
      } else {
        const items = Array.isArray(state[prop]) ? state[prop] : (state[prop] ? [state[prop]] : []);
        isCompleted = items.length >= (step.min_selections || 0);
      }

      // Select appropriate step icon (prioritize metaobject step.icon if set)
      let icon = step.icon || '';
      if (!icon) {
        icon = '🎁';
        if (step.type === 'base') icon = '📦';
        if (step.type === 'flowers') icon = '🌸';
        if (step.type === 'cake') icon = '🎂';
        if (step.type === 'chocolate') icon = '🍫';
        if (step.type === 'balloon') icon = '🎈';
        if (step.type === 'personalization') icon = '✍️';
        if (step.type === 'delivery') icon = '📅';
      }

      const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
      let displayTitle = step.title;
      if (isRtl) {
        const tLow = (step.title || '').toLowerCase();
        if (tLow.includes('base') || tLow.includes('box')) displayTitle = '١. اختر صندوقك';
        else if (tLow.includes('flower') || tLow.includes('rose')) displayTitle = '٢. أضف الزهور';
        else if (tLow.includes('cake')) displayTitle = '٣. كعكة الهدايا';
        else if (tLow.includes('chocolate')) displayTitle = '٤. أضف الشوكولاتة';
        else if (tLow.includes('balloon')) displayTitle = '٥. بالونات الاحتفال';
        else if (tLow.includes('personal')) displayTitle = '٦. تفاصيل التخصيص';
        else if (tLow.includes('extra') || tLow.includes('touch') || tLow.includes('item')) displayTitle = '٧. أضف الملحقات';
        else if (tLow.includes('delivery') || tLow.includes('schedule')) displayTitle = '٨. جدول التوصيل';
      }

      // 1. Render Desktop Sidebar node
      if (sidebar) {
        const node = document.createElement('div');
        node.className = `happe-stepper-node ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`;
        node.innerHTML = `
          <div class="happe-stepper-circle">${isCompleted && !isActive ? '✓' : icon}</div>
          <div class="happe-stepper-label">${displayTitle}</div>
        `;
        node.addEventListener('click', () => {
          if (!state.base && step.type !== 'base') return;
          this.currentStepIndex = idx;
          this.activeOccasionFilter = 'All';
          this.render();
        });
        sidebar.appendChild(node);
      }

      // 2. Render Mobile Scrollable Header node
      if (mobileNav) {
        const mNode = document.createElement('div');
        mNode.className = `mobile-stepper-node ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`;
        mNode.innerHTML = `
          <span class="mobile-stepper-circle">${isCompleted && !isActive ? '✓' : icon}</span>
          <span class="mobile-stepper-label">${displayTitle}</span>
        `;
        mNode.addEventListener('click', () => {
          if (!state.base && step.type !== 'base') return;
          this.currentStepIndex = idx;
          this.activeOccasionFilter = 'All';
          this.render();
        });
        mobileNav.appendChild(mNode);

        // Auto scroll active step into view on mobile viewport
        if (isActive) {
          setTimeout(() => {
            mNode.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }, 100);
        }
      }
    });
  }

  toggleMobileDrawer(isOpen) {
    if (isOpen) {
      this.mobileSummaryDrawer.classList.add('is-open');
      this.mobileSummaryOverlay.classList.add('is-visible');
    } else {
      this.mobileSummaryDrawer.classList.remove('is-open');
      this.mobileSummaryOverlay.classList.remove('is-visible');
    }
  }

  render() {
    if (this.steps.length === 0) return;
    const step = this.steps[this.currentStepIndex];
    const totalSteps = this.steps.length;

    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    let displayTitle = step.title;
    if (isRtl) {
      const tLow = (step.title || '').toLowerCase();
      if (tLow.includes('base') || tLow.includes('box')) displayTitle = 'اختر صندوق الهدايا الأساسي';
      else if (tLow.includes('flower') || tLow.includes('rose')) displayTitle = 'اختر الزهور الطازجة';
      else if (tLow.includes('cake')) displayTitle = 'اختر كعكة الهدايا';
      else if (tLow.includes('chocolate')) displayTitle = 'أضف شوكولاتة فاخرة';
      else if (tLow.includes('balloon')) displayTitle = 'اختر بالونات الاحتفال';
      else if (tLow.includes('personal')) displayTitle = 'تفاصيل التخصيص';
      else if (tLow.includes('extra') || tLow.includes('touch') || tLow.includes('item')) displayTitle = 'أضف الملحقات';
      else if (tLow.includes('delivery') || tLow.includes('schedule')) displayTitle = 'جدول التوصيل';
    }

    // 1. Update Title and Steps progress safely
    if (this.stepTitle) this.stepTitle.textContent = displayTitle;
    if (this.stepIndicator) this.stepIndicator.textContent = isRtl ? `الخطوة ${this.currentStepIndex + 1} من ${totalSteps}` : `Step ${this.currentStepIndex + 1} of ${totalSteps}`;
    
    const percentage = Math.round(((this.currentStepIndex + 1) / totalSteps) * 100);
    if (this.stepPercentage) this.stepPercentage.textContent = `${percentage}%`;
    if (this.progressBar) this.progressBar.style.width = `${percentage}%`;

    // 2. Render Left Sidebar Stepper
    this.renderSidebarStepper();

    // 3. Render content based on step type
    this.renderStepContent(step);

    // 4. Update Navigation visibility
    if (this.prevBtn) this.prevBtn.style.display = this.currentStepIndex === 0 ? 'none' : 'block';
    
    const isLastStep = this.currentStepIndex === totalSteps - 1;
    if (this.nextBtn) this.nextBtn.style.display = isLastStep ? 'none' : 'block';
    if (this.addBtn) this.addBtn.style.display = isLastStep ? 'block' : 'none';
    if (this.skipBtn) this.skipBtn.style.display = (!step.required && !isLastStep) ? 'block' : 'none';

    // 5. Update validation limits
    this.validateCurrentStep();
    
    // 6. Update initial layout displays
    this.updatePricing();
    this.updateSummary();

    // 7. Dispatch step-render event for preview sync
    const event = new CustomEvent('builder:step-render', { 
      detail: { 
        state: window.BuilderState.state, 
        currentStepIndex: this.currentStepIndex,
        steps: this.steps
      } 
    });
    document.dispatchEvent(event);
  }

  renderStepContent(step) {
    try {
      this.stepContent.innerHTML = '';

      // Handle personalization and delivery steps differently
      if (step.type === 'personalization') {
        this.renderPersonalizationForm();
        return;
      }
      if (step.type === 'delivery') {
        this.renderDeliveryCalendar();
        return;
      }

      const state = window.BuilderState.state;

      // Check if the step collection is completely empty (no products exist at all)
      if (!step.options || step.options.length === 0) {
        const emptyMsg = document.createElement('div');
        emptyMsg.style.cssText = 'grid-column: 1 / -1; color: #8c8276; font-size: 0.95rem; font-style: italic; text-align: center; padding: 50px 20px; line-height: 1.6; border: 1.5px dashed var(--builder-gray); border-radius: 12px; background: #faf8f5;';
        
        let collectionName = step.type;
        if (step.type === 'base') collectionName = 'base, base-box, or base-collection';
        if (step.type === 'flowers') collectionName = 'flowers or fresh-flowers';
        if (step.type === 'cake') collectionName = 'cake or cakes';
        if (step.type === 'chocolate') collectionName = 'chocolates or chocolate';
        if (step.type === 'balloon') collectionName = 'balloons or balloon';
        if (step.type === 'extras') collectionName = 'extras';

        emptyMsg.innerHTML = `
          <p style="margin: 0 0 10px 0; font-weight: 600; color: var(--builder-primary);">No products found for this step.</p>
          <p style="margin: 0; font-size: 0.85rem;">To show items here, please create a Collection in your Shopify admin with the handle <strong>"${collectionName}"</strong> and add active products to it.</p>
        `;
        this.stepContent.appendChild(emptyMsg);
        return;
      }

      // Filter options based on active global filters (AND logic between groups, OR within groups)
      let filteredOptions = step.options;
      let showedFallback = false;
      let activeFilterNames = [];

      const activeGroups = [];
      if (Array.isArray(this.activeFilters.ideas) && this.activeFilters.ideas.length > 0) {
        activeGroups.push({ values: this.activeFilters.ideas, type: 'Gift Ideas' });
      } else if (typeof this.activeFilters.ideas === 'string' && this.activeFilters.ideas !== 'All') {
        activeGroups.push({ values: [this.activeFilters.ideas], type: 'Gift Ideas' });
      }

      if (Array.isArray(this.activeFilters.occasion) && this.activeFilters.occasion.length > 0) {
        activeGroups.push({ values: this.activeFilters.occasion, type: 'Occasion' });
      } else if (typeof this.activeFilters.occasion === 'string' && this.activeFilters.occasion !== 'All') {
        activeGroups.push({ values: [this.activeFilters.occasion], type: 'Occasion' });
      }

      if (Array.isArray(this.activeFilters.recipient) && this.activeFilters.recipient.length > 0) {
        activeGroups.push({ values: this.activeFilters.recipient, type: 'Recipient' });
      } else if (typeof this.activeFilters.recipient === 'string' && this.activeFilters.recipient !== 'All') {
        activeGroups.push({ values: [this.activeFilters.recipient], type: 'Recipient' });
      }

      if (activeGroups.length > 0 && step.options && step.options.length > 0) {
        const matched = step.options.filter(item => {
          if (!item.tags || !Array.isArray(item.tags)) return false;
          return activeGroups.every(groupObj => {
            return groupObj.values.some(filterVal => {
              const filterLower = filterVal.trim().toLowerCase();
              return item.tags.some(tag => {
                const tagLower = tag.trim().toLowerCase();
                return tagLower === filterLower || tagLower.includes(filterLower) || filterLower.includes(tagLower);
              });
            });
          });
        });

        if (matched.length > 0) {
          filteredOptions = matched;
        } else {
          showedFallback = true;
          activeGroups.forEach(g => {
            activeFilterNames.push(`${g.type}: [${g.values.join(', ')}]`);
          });
        }
      }

      // If filter returned no results, show fallback notice and render all items instead
      if (showedFallback) {
        const notice = document.createElement('div');
        notice.style.cssText = 'grid-column: 1 / -1; color: #8c8276; font-size: 0.85rem; font-style: italic; background: #faf8f5; padding: 12px 15px; border-radius: 8px; border: 1.5px dashed var(--builder-gray); margin-bottom: 10px; font-family: "Inter", sans-serif;';
        notice.textContent = `Showing all options for this step (No items match: ${activeFilterNames.join(', ')}).`;
        this.stepContent.appendChild(notice);
      }

      // Sort options based on active sort selection
      let finalOptions = [...filteredOptions];
      if (this.activeSort === 'price-low-high') {
        finalOptions.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
      } else if (this.activeSort === 'price-high-low') {
        finalOptions.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
      } else if (this.activeSort === 'newest') {
        finalOptions.reverse();
      }

      finalOptions.forEach(item => {
        const isSel = this.isItemSelected(step.type, item.id);
        const card = document.createElement('div');
        card.className = `item-card ${isSel ? 'selected' : ''}`;
        card.setAttribute('tabindex', '0');
        card.setAttribute('role', 'checkbox');
        card.setAttribute('aria-checked', isSel ? 'true' : 'false');
        
        card.innerHTML = `
          <div class="item-image">
            <img src="${item.thumbnail}" alt="${item.title}" loading="lazy">
          </div>
          <div class="item-info">
            <h3>${item.title}</h3>
            <span class="item-price">${item.price_formatted}</span>
          </div>
          <div class="selected-badge">✓</div>
        `;

        card.addEventListener('click', () => {
          window.BuilderAPI.toggleItem(
            step.type === 'base' ? 'base' : step.type,
            item,
            step.max_selections
          );
          this.renderStepContent(step);
        });

        card.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            card.click();
          }
        });

        this.stepContent.appendChild(card);
      });
    } catch (err) {
      console.error('Error rendering step content:', err);
      this.stepContent.innerHTML = `<div class="error-notice" style="grid-column: 1 / -1; padding: 20px; color: #d9534f; background: #fdf7f7; border: 1px solid #d9534f; border-radius: 8px; font-family: 'Inter', sans-serif;">Error rendering step: ${err.message}</div>`;
    }
  }

  renderPersonalizationForm() {
    const state = window.BuilderState.state.personalization;
    const form = document.createElement('div');
    form.className = 'personalization-form-wrapper';
    form.innerHTML = `
      <div class="form-group">
        <label for="p-recipient">Recipient's Name</label>
        <input type="text" id="p-recipient" class="form-control" value="${state.recipient || ''}" placeholder="Who is this gift for?">
      </div>
      <div class="form-group">
        <label for="p-sender">Sender's Name</label>
        <input type="text" id="p-sender" class="form-control" value="${state.sender || ''}" placeholder="Your name (optional)">
      </div>
      <div class="form-group">
        <label for="p-message">Greeting Card Message</label>
        <textarea id="p-message" class="form-control" rows="4" placeholder="Write a sweet message...">${state.message || ''}</textarea>
      </div>
    `;

    // Map inputs to API
    form.querySelector('#p-recipient').addEventListener('input', (e) => {
      window.BuilderAPI.setPersonalization('recipient', e.target.value);
    });
    form.querySelector('#p-sender').addEventListener('input', (e) => {
      window.BuilderAPI.setPersonalization('sender', e.target.value);
    });
    form.querySelector('#p-message').addEventListener('input', (e) => {
      window.BuilderAPI.setPersonalization('message', e.target.value);
    });

    this.stepContent.appendChild(form);
  }

  renderDeliveryCalendar() {
    const state = window.BuilderState.state.delivery;
    const cal = document.createElement('div');
    cal.className = 'delivery-scheduler-wrapper';
    
    // Generate dates options (next 7 days starting tomorrow)
    const optionsHtml = [];
    const today = new Date();
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const iso = date.toISOString().split('T')[0];
      const display = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
      optionsHtml.push(`<option value="${iso}" ${state.date === iso ? 'selected' : ''}>${display}</option>`);
    }

    cal.innerHTML = `
      <div class="form-group">
        <label for="d-city">Delivery Area (Qatar)</label>
        <select id="d-city" class="form-control">
          <option value="Doha" ${state.city === 'Doha' ? 'selected' : ''}>Doha (Free Delivery)</option>
          <option value="Al Rayyan" ${state.city === 'Al Rayyan' ? 'selected' : ''}>Al Rayyan (QAR 30.00)</option>
          <option value="Al Wakrah" ${state.city === 'Al Wakrah' ? 'selected' : ''}>Al Wakrah (QAR 30.00)</option>
          <option value="Al Khor" ${state.city === 'Al Khor' ? 'selected' : ''}>Al Khor (QAR 30.00)</option>
        </select>
      </div>
      <div class="form-group">
        <label for="d-date">Preferred Delivery Date</label>
        <select id="d-date" class="form-control">
          <option value="">Choose a date...</option>
          ${optionsHtml.join('')}
        </select>
      </div>
      <div class="form-group">
        <label for="d-slot">Available Delivery Time Slot</label>
        <select id="d-slot" class="form-control">
          <option value="">Choose a slot...</option>
          ${this.deliverySlots.map(s => `<option value="${s.start_time} - ${s.end_time}" ${state.slot === `${s.start_time} - ${s.end_time}` ? 'selected' : ''}>${s.start_time} - ${s.end_time} (Cut-off: ${s.cutoff_time})</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label for="d-phone">Recipient Contact Phone Number</label>
        <input type="tel" id="d-phone" class="form-control" value="${state.phone || ''}" placeholder="+974-xxxx-xxxx">
      </div>
    `;

    cal.querySelector('#d-city').addEventListener('change', (e) => {
      window.BuilderAPI.setDelivery('city', e.target.value);
    });
    cal.querySelector('#d-date').addEventListener('change', (e) => {
      window.BuilderAPI.setDelivery('date', e.target.value);
    });
    cal.querySelector('#d-slot').addEventListener('change', (e) => {
      window.BuilderAPI.setDelivery('slot', e.target.value);
    });
    cal.querySelector('#d-phone').addEventListener('input', (e) => {
      window.BuilderAPI.setDelivery('phone', e.target.value);
    });

    this.stepContent.appendChild(cal);
  }

  isItemSelected(type, id) {
    const state = window.BuilderState.state;
    let prop = type;
    if (type === 'chocolate') prop = 'chocolates';
    if (type === 'balloon') prop = 'balloons';
    if (type === 'custom-items' || type === 'custom_items') prop = 'custom_items';

    const getArr = (val) => Array.isArray(val) ? val : (val ? [val] : []);

    if (prop === 'base') return state.base?.id === id;
    if (prop === 'flowers') return getArr(state.flowers).some(i => i && i.id === id);
    if (prop === 'cake') return state.cake?.id === id;
    if (prop === 'chocolates') return getArr(state.chocolates).some(i => i && i.id === id);
    if (prop === 'balloons') return getArr(state.balloons).some(i => i && i.id === id);
    if (prop === 'extras') return getArr(state.extras).some(i => i && i.id === id);
    if (prop === 'custom_items') return getArr(state.custom_items).some(i => i && i.id === id);
    return false;
  }

  validateCurrentStep() {
    if (this.steps.length === 0) return;
    const step = this.steps[this.currentStepIndex];
    if (!step.required) {
      this.nextBtn.disabled = false;
      this.nextBtn.style.opacity = '1';
      return;
    }

    let isValid = false;
    const state = window.BuilderState.state;

    if (step.type === 'base') {
      isValid = !!state.base;
    } else if (step.type === 'personalization') {
      isValid = !!state.personalization.recipient;
    } else if (step.type === 'delivery') {
      isValid = !!state.delivery.date && !!state.delivery.slot && !!state.delivery.phone;
    } else {
      // General selections checks
      let prop = step.type;
      if (step.type === 'chocolate') prop = 'chocolates';
      if (step.type === 'balloon') prop = 'balloons';
      if (step.type === 'custom-items' || step.type === 'custom_items') prop = 'custom_items';
      const items = Array.isArray(state[prop]) ? state[prop] : (state[prop] ? [state[prop]] : []);
      isValid = items.length >= (step.min_selections || 0);
    }

    this.nextBtn.disabled = !isValid;
    this.nextBtn.style.opacity = isValid ? '1' : '0.5';
    this.addBtn.disabled = !isValid;
    this.addBtn.style.opacity = isValid ? '1' : '0.5';
  }

  updatePricing() {
    const total = window.BuilderAPI.calculateTotal();
    const formatted = `QAR ${total.toFixed(2)}`;
    
    if (this.totalPriceEl) this.totalPriceEl.textContent = formatted;
    if (this.mobileTotalPriceEl) this.mobileTotalPriceEl.textContent = formatted;
    if (this.mobileDrawerTotal) this.mobileDrawerTotal.textContent = formatted;

    const headerPriceEl = document.getElementById('header-total-price');
    if (headerPriceEl) headerPriceEl.textContent = formatted;
    const footerPriceEl = document.getElementById('footer-total-price');
    if (footerPriceEl) footerPriceEl.textContent = formatted;
  }

  updateFiltersBadge() {
    const total = this.activeFilters.ideas.length + this.activeFilters.occasion.length + this.activeFilters.recipient.length;
    if (this.filterCountBadge) {
      if (total > 0) {
        this.filterCountBadge.style.display = 'inline-flex';
        this.filterCountBadge.textContent = total;
      } else {
        this.filterCountBadge.style.display = 'none';
      }
    }
  }

  updateSummary() {
    const state = window.BuilderState.state;
    let html = '';

    if (state.base) html += this.renderSummaryRow('Base Box', state.base.title, state.base.price, state.base.handle, state.base.overlay_image);
    if (Array.isArray(state.flowers)) {
      state.flowers.forEach(f => html += this.renderSummaryRow('Flowers', f.title, f.price, f.handle, f.overlay_image));
    }
    if (state.cake) html += this.renderSummaryRow('Cake', state.cake.title, state.cake.price, state.cake.handle, state.cake.overlay_image);
    if (Array.isArray(state.chocolates)) {
      state.chocolates.forEach(c => html += this.renderSummaryRow('Chocolates', c.title, c.price, c.handle, c.overlay_image));
    }
    if (Array.isArray(state.balloons)) {
      state.balloons.forEach(b => html += this.renderSummaryRow('Balloons', b.title, b.price, b.handle, b.overlay_image));
    }
    if (Array.isArray(state.extras)) {
      state.extras.forEach(e => html += this.renderSummaryRow('Add-on', e.title, e.price, e.handle, e.overlay_image));
    }
    if (Array.isArray(state.custom_items)) {
      state.custom_items.forEach(ci => html += this.renderSummaryRow('Custom Item', ci.title, ci.price, ci.handle, ci.overlay_image));
    }

    const finalHtml = html || '<p class="empty-msg">No selections made yet</p>';
    this.summaryList.innerHTML = finalHtml;
    this.mobileSummaryList.innerHTML = finalHtml;
  }

  renderSummaryRow(label, title, price, handle, overlayImage) {
    const titleLink = handle ? `<a href="/products/${handle}" target="_blank" class="summary-item-link" data-image="${overlayImage || ''}" style="color: var(--builder-accent); text-decoration: underline; font-weight: 500; cursor: pointer;">${title}</a>` : title;
    return `
      <div class="summary-item">
        <span class="item-label">${label}: ${titleLink}</span>
        <span class="item-price">QAR ${parseFloat(price).toFixed(2)}</span>
      </div>
    `;
  }
}

// Initialize components on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.GiftBuilderUI = new GiftBuilderUIOrchestrator();
});
